/**
 * Angus and the Music Pantheon — landing page.
 *
 * Visual theme (per user iter 52): early-to-mid-1990s MS-DOS video
 * games.  Think Sierra On-Line / King's Quest / Police Quest VGA
 * boot-screens — chunky pixel borders, fixed-width "VGA" font, hard
 * primary palette (no gradients, no anti-aliased shadows, no smooth
 * box-shadow stacks).
 *
 * Palette is hand-restricted to 12 colours drawn from the canonical
 * VGA / EGA system palette (well within the 64-colour limit the user
 * asked for):
 *   • Background   #000000 (DOS black)
 *   • Border       #AAAAAA (DOS light grey)
 *   • Accent 1     #5555FF (bright blue)
 *   • Accent 2     #FFFF55 (bright yellow — DOS title colour)
 *   • Accent 3     #FF5555 (bright red — alert)
 *   • Accent 4     #55FF55 (bright green — OK)
 *   • Accent 5     #55FFFF (bright cyan)
 *   • Accent 6     #FF55FF (bright magenta)
 *   • Mid grey     #555555
 *   • Dark blue    #0000AA (dialog bg)
 *   • Dark cyan    #00AAAA (panel rule)
 *   • Body text    #FFFFFF (white)
 *
 * No drop shadows, no gradients, no rounded corners.  Borders are
 * one solid colour, double or single line only.  Typography is
 * "VT323"/"Press Start 2P"/Lucida Console fixed-pitch — a Google
 * font fallback chain that always lands on something monospace.
 */
import { useNavigate } from "react-router-dom";
import TopBar from "@/components/TopBar";
import NavPane from "@/components/NavPane";
import ANGUS_WRITING from "@/lib/angusWriting";

const PAL = {
  bg:         "#000000",
  borderHi:   "#AAAAAA",
  borderLo:   "#555555",
  dialogBg:   "#0000AA",
  panelRule:  "#00AAAA",
  title:      "#FFFF55",
  alert:      "#FF5555",
  ok:         "#55FF55",
  cyan:       "#55FFFF",
  magenta:    "#FF55FF",
  body:       "#FFFFFF",
};

const VGA_FONT = "'VT323','Press Start 2P','Lucida Console','Courier New',monospace";

const AngusPage = () => {
  const navigate = useNavigate();

  return (
    <div
      className="relative min-h-screen overflow-x-clip"
      data-testid="angus-page"
      style={{
        background: PAL.bg,
        color: PAL.body,
        fontFamily: VGA_FONT,
        // Hard pixel rendering — no anti-alias smoothing on the borders
        // and pseudo-pixel art elements.
        imageRendering: "pixelated",
      }}
    >
      <TopBar
        theme="studio"
        onBack={() => navigate("/")}
        backLabel="HOME"
        testId="angus-back-button"
      />

      <div className="relative z-10 min-h-screen flex flex-col md:flex-row max-w-7xl mx-auto">
        {/* LEFT NAV PANE — kept, NavPane styles itself per theme */}
        <div
          className="md:w-[260px] md:shrink-0 md:min-h-screen flex flex-col pt-4 md:pt-6 px-4 md:px-6 pb-6 md:border-r"
          style={{ borderColor: PAL.borderLo }}
          data-testid="angus-left-pane"
        >
          <NavPane theme="studio" />
        </div>

        <main className="flex-1 flex flex-col pt-4 md:pt-6 px-6 md:px-12 pb-10">
          <p
            className="max-w-3xl mb-6"
            style={{
              fontFamily: VGA_FONT,
              color: PAL.cyan,
              fontSize: "0.95rem",
              letterSpacing: "0.04em",
              lineHeight: 1.55,
            }}
            data-testid="angus-blurb"
          >
            Emergent here. Andrew asked me to build a pre-production landing page in true MS-DOS style
            &mdash; VGA font, 64-colour EGA-feel palette, AdLib-style 80s MIDI groove &mdash; and I
            synthesised the music procedurally and authored the entire screen in CSS without a single
            bitmap.
          </p>
          {/* DOS-style window frame.  Hard double border, no corners. */}
          <div
            className="max-w-3xl"
            style={{
              border: `4px double ${PAL.borderHi}`,
              padding: "1.5rem",
              background: PAL.bg,
              boxShadow: `inset 0 0 0 1px ${PAL.borderLo}`,
            }}
          >
            <div
              className="text-xs mb-5"
              style={{
                color: PAL.cyan,
                fontFamily: VGA_FONT,
                letterSpacing: "0.10em",
                textTransform: "uppercase",
                fontSize: "0.95rem",
              }}
              data-testid="angus-eyebrow"
            >
              IN DEVELOPMENT &middot; PRE-PRODUCTION
            </div>

            <h1
              className="leading-[1.05] mb-6"
              style={{
                fontFamily: VGA_FONT,
                fontWeight: 400,
                color: PAL.title,
                fontSize: "clamp(2rem, 4vw, 3.5rem)",
                letterSpacing: "0.02em",
                textTransform: "uppercase",
              }}
              data-testid="angus-title"
              aria-label="Angus and the Music Pantheon"
            >
              Angus and the Music Pantheon
            </h1>

            <p
              className="max-w-3xl mb-8"
              style={{
                fontFamily: VGA_FONT,
                fontSize: "1.25rem",
                color: PAL.body,
                lineHeight: 1.45,
                letterSpacing: "0.01em",
              }}
              data-testid="angus-tagline"
            >
              This game is still in development.  Below: the design document, the flowchart,
              and the working draft of the in-game writing &mdash; in their current state.
            </p>

            {/* DESIGN DOCUMENT — replaces the former COMING SOON section. */}
            <section
              className="mb-4"
              style={{
                border: `2px solid ${PAL.panelRule}`,
                padding: "1rem 1.25rem",
                background: PAL.bg,
              }}
              data-testid="angus-design-doc-canvas"
            >
              <div
                className="mb-3"
                style={{
                  color: PAL.alert,
                  fontFamily: VGA_FONT,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  fontSize: "1.05rem",
                }}
              >
                {">"} DESIGN DOCUMENT
              </div>
              <ul
                className="pl-2 space-y-2"
                style={{
                  color: PAL.body,
                  fontFamily: VGA_FONT,
                  fontSize: "1.15rem",
                  lineHeight: 1.45,
                  listStyle: "none",
                }}
                data-testid="angus-design-doc-list"
              >
                <li>
                  <span style={{ color: PAL.ok }}>{"[*]"}</span>{" "}
                  Late 80s &ndash; early 90s MS-DOS vibes &mdash; graphics, music, and sound
                </li>
                <li>
                  <span style={{ color: PAL.ok }}>{"[*]"}</span>{" "}
                  Profanity
                </li>
                <li>
                  <span style={{ color: PAL.ok }}>{"[*]"}</span>{" "}
                  Humor
                </li>
                <li>
                  <span style={{ color: PAL.ok }}>{"[*]"}</span>{" "}
                  Gore
                </li>
                <li>
                  <span style={{ color: PAL.ok }}>{"[*]"}</span>{" "}
                  Gun violence
                </li>
              </ul>
              <div
                className="mt-4"
                style={{
                  color: PAL.title,
                  fontFamily: VGA_FONT,
                  fontSize: "1.15rem",
                  lineHeight: 1.45,
                  letterSpacing: "0.02em",
                }}
                data-testid="angus-mvgfa"
              >
                &ldquo;MVGFA&rdquo; &mdash; Make Video Games Fun Again
              </div>
              <div
                className="mt-2"
                style={{
                  color: PAL.body,
                  fontFamily: VGA_FONT,
                  fontSize: "1.15rem",
                  lineHeight: 1.45,
                }}
                data-testid="angus-levels"
              >
                3 days (&ldquo;levels&rdquo;)
              </div>
            </section>

            {/* MAP DEMO — inserted between the design document sub-canvas
                and the flowchart image per user iter. */}
            <div
              className="mb-4"
              style={{
                border: `2px solid ${PAL.panelRule}`,
                padding: "0.5rem",
                background: "#FFFFFF",
              }}
              data-testid="angus-map-demo-frame"
            >
              <img
                src="/angus/angus_map_demo_d.jpg"
                alt="Angus and the Music Pantheon — map demo"
                data-testid="angus-map-demo-img"
                style={{
                  display: "block",
                  width: "100%",
                  height: "auto",
                  imageRendering: "auto",
                }}
              />
            </div>

            {/* FLOWCHART — squeezed between the design doc and the writing canvas */}
            <div
              className="mb-4"
              style={{
                border: `2px solid ${PAL.panelRule}`,
                padding: "0.5rem",
                background: "#FFFFFF",
              }}
              data-testid="angus-flowchart-frame"
            >
              <img
                src="/angus/flowchart_custom_2.png"
                alt="Angus and the Music Pantheon — game flowchart"
                data-testid="angus-flowchart-img"
                style={{
                  display: "block",
                  width: "100%",
                  height: "auto",
                  imageRendering: "auto",
                }}
              />
            </div>

            {/* IMG_3756 — additional reference image inserted between the
                flowchart and the writing draft per user iter. */}
            <div
              className="mb-4"
              style={{
                border: `2px solid ${PAL.panelRule}`,
                padding: "0.5rem",
                background: "#FFFFFF",
              }}
              data-testid="angus-img3756-frame"
            >
              <img
                src="/angus/IMG_3756.jpeg"
                alt="Angus and the Music Pantheon — additional reference"
                data-testid="angus-img3756-img"
                style={{
                  display: "block",
                  width: "100%",
                  height: "auto",
                  imageRendering: "auto",
                }}
              />
            </div>

            {/* MASTER — additional reference page inserted between the
                five-name-figures sketch (IMG_3756) and the writing
                draft canvas per user iter-110. */}
            <div
              className="mb-4"
              style={{
                border: `2px solid ${PAL.panelRule}`,
                padding: "0.5rem",
                background: "#FFFFFF",
              }}
              data-testid="angus-master-frame"
            >
              <img
                src="/angus/master.jpeg"
                alt="Angus and the Music Pantheon — master design page"
                data-testid="angus-master-img"
                style={{
                  display: "block",
                  width: "100%",
                  height: "auto",
                  imageRendering: "auto",
                }}
              />
            </div>

            {/* WRITING — scrollable sub-canvas with the working in-game text */}
            <section
              className="mb-2"
              style={{
                border: `2px solid ${PAL.panelRule}`,
                padding: "1rem 1.25rem",
                background: PAL.bg,
              }}
              data-testid="angus-writing-canvas"
            >
              <div
                className="mb-3"
                style={{
                  color: PAL.alert,
                  fontFamily: VGA_FONT,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  fontSize: "1.05rem",
                }}
              >
                {">"} WRITING &mdash; WORKING DRAFT
              </div>
              <div
                style={{
                  maxHeight: "420px",
                  overflowY: "auto",
                  paddingRight: "0.75rem",
                  border: `1px solid ${PAL.borderLo}`,
                  background: PAL.bg,
                  color: PAL.body,
                  fontFamily: VGA_FONT,
                  fontSize: "1.05rem",
                  lineHeight: 1.55,
                }}
                data-testid="angus-writing-scroll"
              >
                <pre
                  style={{
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    margin: 0,
                    padding: "0.9rem 1rem",
                    fontFamily: VGA_FONT,
                    fontSize: "1.05rem",
                    lineHeight: 1.55,
                    color: PAL.body,
                  }}
                  data-testid="angus-writing-text"
                >{ANGUS_WRITING}</pre>
              </div>
            </section>
          </div>
        </main>
      </div>
    </div>
  );
};

export default AngusPage;
